import { EmailCapture } from '../components/EmailCapture';
import { SEO } from '../components/SEO';
import './ExclusiveDealsPage.css';

export function ExclusiveDealsPage() {
  return (
    <>
      <SEO
        title="Exclusive Peptide Deals - Private Discounts & Price Alerts | Peptide Finder"
        description="Get exclusive peptide deals, private discount codes, and price-drop alerts. Members receive flash deals, vendor launches, and best price alerts you won't find anywhere else."
        keywords="exclusive peptide deals, peptide discounts, price alerts, private coupon codes, peptide price drops"
      />
      <div className="exclusive-deals-page">
        {/* 1. Hero Section */}
        <section className="exclusive-deals-hero">
          <div className="exclusive-deals-container">
            <h1 className="exclusive-deals-headline">
              Get Private Peptide Deals You Won't Find Anywhere Else
            </h1>
            <p className="exclusive-deals-subheadline">
              Members get exclusive discount codes, limited-time price drops, and early alerts when peptide prices fall.
            </p>
            <p className="exclusive-deals-trust-line">
              Trusted by researchers using Peptide Finder to compare prices across verified vendors
            </p>
            <div className="exclusive-deals-hero-cta">
              <EmailCapture
                buttonText="Get Exclusive Deals"
                placeholder="📩 Enter your email to unlock exclusive deals"
                showMicroCopy={true}
              />
            </div>
          </div>
        </section>

        {/* 2. What You Get Section */}
        <section className="exclusive-deals-section">
          <div className="exclusive-deals-container">
            <h2 className="exclusive-deals-section-title">
              🔓 As a member, you'll receive:
            </h2>
            <ul className="exclusive-deals-value-stack">
              <li className="exclusive-deals-benefit">
                <span className="exclusive-deals-benefit-icon">🔑</span>
                <div>
                  <strong>Private vendor discount codes</strong> (not publicly listed)
                </div>
              </li>
              <li className="exclusive-deals-benefit">
                <span className="exclusive-deals-benefit-icon">📉</span>
                <div>
                  <strong>Price-drop alerts</strong> when peptide costs fall
                </div>
              </li>
              <li className="exclusive-deals-benefit">
                <span className="exclusive-deals-benefit-icon">⚡</span>
                <div>
                  <strong>Flash deals</strong> (24–72 hour vendor promos)
                </div>
              </li>
              <li className="exclusive-deals-benefit">
                <span className="exclusive-deals-benefit-icon">🆕</span>
                <div>
                  <strong>New vendor launches</strong> & exclusive offers
                </div>
              </li>
              <li className="exclusive-deals-benefit">
                <span className="exclusive-deals-benefit-icon">💰</span>
                <div>
                  <strong>Best price per mg alerts</strong> for peptides you track
                </div>
              </li>
            </ul>
            <p className="exclusive-deals-coming-soon">
              🎯 <strong>Coming soon:</strong> personalized alerts for peptides you follow
            </p>
          </div>
        </section>

        {/* 3. How It Works Section */}
        <section className="exclusive-deals-section exclusive-deals-section-alt">
          <div className="exclusive-deals-container">
            <h2 className="exclusive-deals-section-title">How It Works</h2>
            <div className="exclusive-deals-steps">
              <div className="exclusive-deals-step">
                <div className="exclusive-deals-step-number">1️⃣</div>
                <h3 className="exclusive-deals-step-title">Join the list</h3>
                <p className="exclusive-deals-step-description">
                  Enter your email (no account required)
                </p>
              </div>
              <div className="exclusive-deals-step">
                <div className="exclusive-deals-step-number">2️⃣</div>
                <h3 className="exclusive-deals-step-title">We monitor prices</h3>
                <p className="exclusive-deals-step-description">
                  Peptide Finder tracks vendors, pricing, and discounts daily
                </p>
              </div>
              <div className="exclusive-deals-step">
                <div className="exclusive-deals-step-number">3️⃣</div>
                <h3 className="exclusive-deals-step-title">You get alerts</h3>
                <p className="exclusive-deals-step-description">
                  When a deal drops — we email you instantly
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 4. Why Exclusive Section */}
        <section className="exclusive-deals-section">
          <div className="exclusive-deals-container">
            <h2 className="exclusive-deals-section-title">
              Why These Deals Are Exclusive
            </h2>
            <p className="exclusive-deals-exclusive-text">
              Peptide Finder partners directly with select research vendors.
              Some deals are private, time-limited, or only available to our users — and cannot be posted publicly.
            </p>
            <ul className="exclusive-deals-exclusive-bullets">
              <li>Vendor-approved private codes</li>
              <li>Limited redemption quantities</li>
              <li>Early access before public release</li>
            </ul>
          </div>
        </section>

        {/* 5. Trust & Credibility Section */}
        <section className="exclusive-deals-section exclusive-deals-section-alt">
          <div className="exclusive-deals-container">
            <h2 className="exclusive-deals-section-title">Trust & Credibility</h2>
            <div className="exclusive-deals-trust-badges">
              <div className="exclusive-deals-trust-badge">
                <span className="exclusive-deals-trust-icon">🔬</span>
                <div>
                  <strong>Research-focused platform</strong>
                </div>
              </div>
              <div className="exclusive-deals-trust-badge">
                <span className="exclusive-deals-trust-icon">📊</span>
                <div>
                  <strong>Price comparison</strong> across multiple vendors
                </div>
              </div>
              <div className="exclusive-deals-trust-badge">
                <span className="exclusive-deals-trust-icon">🔒</span>
                <div>
                  <strong>Your email is never sold</strong> or shared
                </div>
              </div>
              <div className="exclusive-deals-trust-badge">
                <span className="exclusive-deals-trust-icon">⚖️</span>
                <div>
                  <strong>Independent vendor comparison</strong> (not pay-to-rank)
                </div>
              </div>
            </div>
            <p className="exclusive-deals-trust-stat">
              Thousands of researchers use Peptide Finder to find the lowest peptide prices.
            </p>
          </div>
        </section>

        {/* 6. Second Email Capture CTA */}
        <section className="exclusive-deals-section exclusive-deals-cta-section">
          <div className="exclusive-deals-container">
            <h2 className="exclusive-deals-cta-headline">
              Don't Miss the Next Price Drop
            </h2>
            <p className="exclusive-deals-cta-subheadline">
              📩 Get exclusive peptide deals straight to your inbox
            </p>
            <div className="exclusive-deals-cta-form">
              <EmailCapture
                buttonText="Unlock Deals Now"
                placeholder="Enter your email"
                showMicroCopy={true}
              />
            </div>
          </div>
        </section>

        {/* 7. Optional Sections */}
        <section className="exclusive-deals-section">
          <div className="exclusive-deals-container">
            <div className="exclusive-deals-optional-grid">
              <div className="exclusive-deals-optional-item">
                <h3 className="exclusive-deals-optional-title">
                  🧠 Popular Deal Alerts Include
                </h3>
                <ul className="exclusive-deals-peptide-list">
                  <li>BPC-157</li>
                  <li>TB-500</li>
                  <li>CJC-1295 / Ipamorelin</li>
                  <li>NAD+</li>
                  <li>GLP-1 research peptides</li>
                </ul>
              </div>
              <div className="exclusive-deals-optional-item">
                <h3 className="exclusive-deals-optional-title">
                  🔔 Email Frequency
                </h3>
                <p className="exclusive-deals-frequency-text">
                  Typically <strong>1–3 emails per week</strong>.
                  <br />
                  Only when deals are worth sending.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 8. Legal / Compliance Footer */}
        <section className="exclusive-deals-legal">
          <div className="exclusive-deals-container">
            <p className="exclusive-deals-disclaimer">
              <strong>Disclaimer:</strong> Peptide Finder is a research price comparison and alert platform.
              We do not sell peptides and do not provide medical advice.
              All products referenced are for research purposes only.
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
